import { inject, Injectable } from "@angular/core";

import { IEmittedVariable } from "@main/reporting.v3/core/variables/variable-models";
import { IQueryPageRequest } from "@main/reporting.v3/widget.grid/widget-grid.models";
import { JsReportConfiguration } from "@main/pdf-exporting/js-report/js-report.models";
import { PdfExportSettings, PdfExportSource } from "@main/reporting.v3/core/reporting-models";
import { VariableRuleService } from "@main/reporting.v3/core/variables/variable-rule.service";
import { HtmlRenderingService } from "@main/reporting.v3/core/rendering/html-rendering.service";

import { HtmlCssClass, HtmlPdfContext } from "@main/reporting.v3/widget.html/models";
import {
    WidgetHtmlEmbeddedWidgetsService
} from "@main/reporting.v3/widget.html/services/widget-html-embedded-widgets.service";


@Injectable()
export class WidgetHtmlPdfExportingService {

    private embeddedWidgetService: WidgetHtmlEmbeddedWidgetsService = inject(WidgetHtmlEmbeddedWidgetsService);
    private htmlRenderingService: HtmlRenderingService = inject(HtmlRenderingService);
    private variableRuleService: VariableRuleService = inject(VariableRuleService);

    public async getPdfExportSettings(context: HtmlPdfContext): Promise<PdfExportSettings> {
        const configuration: JsReportConfiguration = await this.getPdfConfiguration(context);

        const variables: IEmittedVariable[] = [
            ...this.variableRuleService.toEmittedVariables(context.staticVariables),
            ...(context.emittedVariables ?? [])
        ];

        const request: IQueryPageRequest = {
            queryVariables: { variables: variables },
            gridSettings: { take: 100_000 },
        };

        const fileName: string = context.widgetName;

        return {
            source: PdfExportSource.WidgetHtml,
            configuration: configuration,
            queryPageRequest: request,
            fileName: fileName,
        };
    }

    public async getPdfConfiguration(context: HtmlPdfContext): Promise<JsReportConfiguration> {
        const template: string = await this.getHtmlContent(context);
        const cssRules: string = this.getCssRules();

        const footerTemplate: string = this.getFooterTemplate();
        const footerCssRules: string = this.getFooterCssRules();

        return {
            template: template,
            cssRules: cssRules,
            footerTemplate: footerTemplate,
            footerCssRules: footerCssRules,
            marginTop: 20,
            marginLeft: 20,
            marginRight: 20,
            marginBottom: 50,
        };
    }

    private async getHtmlContent(context: HtmlPdfContext): Promise<string> {
        if (!context?.template?.length) return "";

        const parser: DOMParser = new DOMParser();
        const doc: Document = parser.parseFromString(context.template, "text/html");

        // Add PDF view container class to widget container class list.
        const selector: string = `.${HtmlCssClass.WidgetContainer}`;

        doc.querySelector(selector)?.classList?.add(HtmlCssClass.PdfView);

        // Change each occurrence of widgets by their PDF templates.
        await this.embeddedWidgetService.replaceWidgetsByPdfTemplates(doc.body);

        // Inject the css rules inside the resulting document.
        this.htmlRenderingService.insertScopedStyles(context.cssInjectionScopeId, doc.body, context.cssRules);

        return doc.body?.innerHTML;
    }

    private getCssRules(): string {
        const selector: string = `.${HtmlCssClass.WidgetContainer}`;

        const cssRules: string = this.htmlRenderingService.getCssRules(selector);

        const bodyCssStyles: string = this.htmlRenderingService.getCssRules("body");

        return `${cssRules}\n${bodyCssStyles}`;
    }


    private getFooterTemplate(): string {
        return `<div class='${HtmlCssClass.WidgetFooter} ${HtmlCssClass.PdfView}'>
                    Page <span class='pageNumber'></span> of <span class='totalPages'></span>
                </div>`;
    }

    private getFooterCssRules(): string {
        const selector: string = `.${HtmlCssClass.WidgetFooter}.${HtmlCssClass.PdfView}`;

        return this.htmlRenderingService.getCssRules(selector);
    }
}